import React, { useState, useEffect, useCallback, useRef } from 'react';
import { assetService } from '../api/assetService';
import { websocketService } from '../api/websocketService';
import { ProcessingResponse, ProcessingStartResponse, AssetStatus, TokenUsage } from '../types/asset';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import TokenUsageDisplay from './TokenUsageDisplay';

const Container = styled.div`
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
`;

const Card = styled.div`
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
`;

const StatusCard = styled(Card)`
    margin-top: 20px;
`;

const Title = styled.h2`
    margin: 0 0 20px;
    color: #333;
`;

const StatusTitle = styled.h3`
    margin: 0 0 15px;
    color: #333;
`;

const InputGroup = styled.div`
    display: flex;
    gap: 10px;
`;

const Input = styled.input`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
`;

const Button = styled.button`
    padding: 8px 16px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const ErrorMessage = styled.div`
    color: #dc3545;
    margin-top: 10px;
`;

const StatusList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StatusItem = styled.div`
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const AssetTitle = styled.span`
    font-weight: 500;
`;

const StatusBadge = styled.span<{ $status: 'completed' | 'failed' | 'processing' | 'info' | 'stopped' }>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 14px;
    background-color: ${props => {
        switch (props.$status) {
            case 'completed':
                return '#28a745';
            case 'failed':
                return '#dc3545';
            case 'processing':
                return '#17a2b8';
            case 'stopped':
                return '#6c757d';
            default:
                return '#6c757d';
        }
    }};
    color: white;
`;

const BackLink = styled(Link)`
    display: inline-block;
    margin-bottom: 20px;
    color: #007bff;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const ProgressBar = styled.div<{ $progress: number }>`
    width: 100%;
    height: 20px;
    background-color: #e9ecef;
    border-radius: 4px;
    margin-top: 10px;
    position: relative;
    overflow: hidden;

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.$progress}%;
        background-color: #007bff;
        transition: width 0.3s ease;
    }
`;

interface ProjectStatus {
    project_id: string;
    status: {
        total_assets: number;
        processed_assets: number;
        failed_assets: number;
        in_progress_assets: number;
        status_summary: {
            pending: number;
            processing: number;
            completed: number;
            failed: number;
            stopped: number;
        };
    };
    token_usage?: TokenUsage;
}

type StatusResponse = ProcessingResponse | ProjectStatus | ProcessingStartResponse;

function isProjectStatus(response: StatusResponse): response is ProjectStatus {
    return 'project_id' in response && 'status' in response;
}

export const AssetProcessor: React.FC = () => {
    const [projectId, setProjectId] = useState<string>('');
    const [processingStatus, setProcessingStatus] = useState<StatusResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [assets, setAssets] = useState<AssetStatus[]>([]);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
    const [isProcessingComplete, setIsProcessingComplete] = useState<boolean>(false);
    const [latestTokenUsage, setLatestTokenUsage] = useState<TokenUsage | null>(null); // Track latest token usage
    const latestTokenUsageRef = useRef<TokenUsage | null>(null); // Use useRef to track latest token usage
    
    // Add state for profile and version selection
    const [availableProfiles, setAvailableProfiles] = useState<any[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<string>('');
    const [selectedVersion, setSelectedVersion] = useState<string>('');
    const [profilesLoading, setProfilesLoading] = useState<boolean>(false);

    // Function to fetch final token usage from API
    const fetchFinalTokenUsage = useCallback(async (projectId: string) => {
        try {
            console.log('[AssetProcessor] Fetching final token usage for project:', projectId);
            const response = await assetService.getProcessingStatus(projectId);
            
            // Handle the actual API response structure (which has statuses field)
            const responseWithStatuses = response as any;
            if (responseWithStatuses && responseWithStatuses.statuses && Array.isArray(responseWithStatuses.statuses)) {
                let totalTokens = 0;
                let totalPromptTokens = 0;
                let totalCompletionTokens = 0;
                let totalApiCalls = 0;
                
                for (const status of responseWithStatuses.statuses as any[]) {
                    if (status.metadata?.token_usage) {
                        const tokenUsage = status.metadata.token_usage;
                        totalTokens += tokenUsage.total_tokens || 0;
                        totalPromptTokens += tokenUsage.total_prompt_tokens || 0;
                        totalCompletionTokens += tokenUsage.total_completion_tokens || 0;
                        totalApiCalls += tokenUsage.api_calls_count || 0;
                    }
                }
                
                if (totalTokens > 0) {
                    const finalTokenUsage: TokenUsage = {
                        total_tokens: totalTokens,
                        total_prompt_tokens: totalPromptTokens,
                        total_completion_tokens: totalCompletionTokens,
                        api_calls_count: totalApiCalls
                    };
                    
                    console.log('[AssetProcessor] Final token usage from API:', finalTokenUsage);
                    return finalTokenUsage;
                }
            }
            
            console.log('[AssetProcessor] No token usage found in API response');
            return null;
        } catch (error) {
            console.error('[AssetProcessor] Error fetching final token usage:', error);
            return null;
        }
    }, []);

    // Load available profiles for the project
    const loadProfiles = useCallback(async () => {
            try {
                setProfilesLoading(true);
            
            // Use the complete prompt management API that loads both default and custom profiles
                const response = await fetch('/api/prompts/profiles');
                if (response.ok) {
                    const data = await response.json();
                console.log('[AssetProcessor] Loaded profiles from API:', data);
                
                // Extract profiles from the response
                let profiles = [];
                if (data.profiles && Array.isArray(data.profiles)) {
                    profiles = data.profiles;
                } else if (Array.isArray(data)) {
                    profiles = data;
                }
                
                console.log('[AssetProcessor] Processed profiles:', profiles);
                
                // Filter profiles based on project access if project ID is provided
                if (projectId.trim()) {
                    profiles = profiles.filter((profile: any) => {
                        // Check if profile allows this project
                        const allowedProjects = profile.allowed_project_ids || profile.metadata?.allowed_project_ids || [];
                        return allowedProjects.length === 0 || allowedProjects.includes(projectId);
                    });
                }
                
                // Add default profiles if no custom ones found or if no project ID
                if (profiles.length === 0 || !projectId.trim()) {
                    profiles.push(
                        {
                            id: "default_image",
                            name: "Default Image Analysis",
                            prompt_type: "image",
                            asset_type: "mixed",
                            description: "Default image analysis prompt",
                            version: "1.0.0",
                            is_default: true
                        },
                        {
                            id: "default_video",
                            name: "Default Video Analysis", 
                            prompt_type: "video",
                            asset_type: "mixed",
                            description: "Default video analysis prompt",
                            version: "1.0.0",
                            is_default: true
                        }
                    );
                }
                
                setAvailableProfiles(profiles);
                
                // Only set default selection if no profile is currently selected
                if (profiles.length > 0 && !selectedProfile) {
                    const defaultProfile = profiles.find((p: any) => p.is_default) || profiles[0];
                    setSelectedProfile(defaultProfile.id || defaultProfile.profile_id || '');
                    setSelectedVersion(defaultProfile.version || '1.0.0');
                }
            } else {
                console.error('[AssetProcessor] Failed to load profiles:', response.status, response.statusText);
                setAvailableProfiles([]);
                // Don't reset selected profile on error to preserve user selection
            }
        } catch (error) {
            console.error('[AssetProcessor] Error loading profiles:', error);
            setAvailableProfiles([]);
            // Don't reset selected profile on error to preserve user selection
        } finally {
            setProfilesLoading(false);
        }
    }, [projectId, selectedProfile]);

    // Load profiles only once on component mount, not on every projectId change
    useEffect(() => {
        loadProfiles();
    }, []); // Empty dependency array - only run once on mount

    const updateStatus = useCallback((data: ProcessingResponse) => {
        console.log('[AssetProcessor] Processing aggregated status update:', {
            total_assets: data.status.total_assets,
            processed_assets: data.status.processed_assets,
            failed_assets: data.status.failed_assets,
            in_progress_assets: data.status.in_progress_assets,
            asset_count: data.assets?.length || 0
        });
        
        // Update the processing status with the new data
        setProcessingStatus(data);
        
        // Track the latest token usage from any message
        if (data.status.token_usage) {
            console.log('[AssetProcessor] Found token usage in status, storing as latest:', data.status.token_usage);
            console.log('[AssetProcessor] Token usage details:', {
                total_tokens: data.status.token_usage.total_tokens,
                total_prompt_tokens: data.status.token_usage.total_prompt_tokens,
                total_completion_tokens: data.status.token_usage.total_completion_tokens,
                api_calls_count: data.status.token_usage.api_calls_count
            });
            setLatestTokenUsage(data.status.token_usage);
            latestTokenUsageRef.current = data.status.token_usage; // Update ref as well
        } else {
            console.log('[AssetProcessor] No token usage found in status');
        }
        
        // Also check for token usage in individual asset updates
        if (data.assets && data.assets.length > 0) {
            for (const asset of data.assets) {
                if (asset.token_usage) {
                    console.log('[AssetProcessor] Found token usage in asset, storing as latest:', asset.token_usage);
                    setLatestTokenUsage(asset.token_usage);
                    latestTokenUsageRef.current = asset.token_usage; // Update ref as well
                    break; // Use the first asset with token usage
                }
            }
        }
        
        // Handle batch status updates (initial or full update)
        if (data.assets && data.assets.length > 0) {
            // If this is a batch update, replace the entire list
            setAssets(data.assets);
        } 
        // Handle individual asset updates
        else if (data.assets && data.assets.length === 1) {
            setAssets(prevAssets => {
                const updatedAssets = [...prevAssets];
                const updatedAsset = data.assets![0]; // We know it exists because of the length check
                const index = updatedAssets.findIndex(asset => asset.id === updatedAsset.id);
                
                if (index !== -1) {
                    // Update existing asset
                    updatedAssets[index] = {
                        ...updatedAssets[index],
                        ...updatedAsset,
                        status: updatedAsset.status || updatedAssets[index].status,
                        progress: updatedAsset.progress || updatedAssets[index].progress
                    };
                } else {
                    // Add new asset
                    updatedAssets.push(updatedAsset);
                }
                
                return updatedAssets;
            });
        }
        
        // Check if processing is complete (all assets processed or failed)
        if (data.status.processed_assets + data.status.failed_assets === data.status.total_assets && 
            data.status.in_progress_assets === 0) {
            console.log('[AssetProcessor] Processing complete detected:', {
                processed: data.status.processed_assets,
                failed: data.status.failed_assets,
                total: data.status.total_assets,
                in_progress: data.status.in_progress_assets,
                has_latest_token_usage: !!latestTokenUsage
            });
            setIsLoading(false);
        }
        
        // Also check if all assets are completed (alternative condition)
        if (data.status.status_summary?.processing === 0) {
            setIsLoading(false);
        }
    }, [latestTokenUsage]);

    const setupWebSocketConnection = useCallback(async () => {
        try {
            console.log('[AssetProcessor] Setting up WebSocket connection');
            
            // Configure status update debounce for smoother UI updates
            websocketService.setStatusUpdateDebounce(1000); // 1000ms debounce for responsive but stable updates
            
            // Set up event handlers
            websocketService.onStatusUpdate((data) => {
                console.log('[AssetProcessor] Received status update:', data);
                updateStatus(data);
            });

            websocketService.onError((errorMessage) => {
                console.error('[AssetProcessor] WebSocket error:', errorMessage);
                setError(errorMessage);
                setIsLoading(false);
            });

            websocketService.onComplete(async () => {
                console.log('[AssetProcessor] Processing complete');
                console.log('[AssetProcessor] Current latestTokenUsageRef:', latestTokenUsageRef.current);
                setIsLoading(false);
                
                // Show the latest token usage after a delay
                setTimeout(async () => {
                    console.log('[AssetProcessor] Checking token usage after delay...');
                    console.log('[AssetProcessor] latestTokenUsageRef.current:', latestTokenUsageRef.current);
                    
                    let finalTokenUsage = latestTokenUsageRef.current;
                    
                    if (!finalTokenUsage) {
                        console.log('[AssetProcessor] No token usage from WebSocket, trying processingStatus...');
                        // Try to get token usage from the current processing status
                        if (processingStatus && 
                            typeof processingStatus.status === 'object' && 
                            processingStatus.status !== null && 
                            'token_usage' in (processingStatus.status as any) && 
                            (processingStatus.status as any).token_usage) {
                            console.log('[AssetProcessor] Found token usage in processingStatus:', (processingStatus.status as any).token_usage);
                            finalTokenUsage = (processingStatus.status as any).token_usage;
                        }
                    }
                    
                    if (!finalTokenUsage) {
                        console.log('[AssetProcessor] No token usage found in WebSocket or processingStatus, fetching from API...');
                        // Fetch final token usage from API
                        finalTokenUsage = await fetchFinalTokenUsage(projectId);
                    }
                    
                    if (finalTokenUsage) {
                        console.log('[AssetProcessor] Displaying final token usage:', finalTokenUsage);
                        setTokenUsage(finalTokenUsage);
                        setIsProcessingComplete(true);
                    } else {
                        console.log('[AssetProcessor] No token usage found anywhere');
                        setIsProcessingComplete(true);
                    }
                }, 2000); // Wait 2 seconds before showing token usage
                
                // Manually disconnect after a delay to ensure final token usage is received
                setTimeout(async () => {
                    try {
                        console.log('[AssetProcessor] Manually disconnecting after completion');
                        await websocketService.disconnect();
                        console.log('[AssetProcessor] Manual disconnect completed');
                    } catch (error) {
                        console.error('[AssetProcessor] Error during manual disconnect:', error);
                    }
                }, 3000); // Wait 3 seconds for final token usage
            });

            // Handle WebSocket connection state
            websocketService.onConnectionStatusChange((connected) => {
                console.log('[AssetProcessor] Connection status changed:', connected);
                setIsConnected(connected);
                if (!connected) {
                    setError('Connection lost. Please try again.');
                } else {
                    setError(null);
                }
            });

            // Establish the WebSocket connection with retry
            let retryCount = 0;
            const maxRetries = 3;
            const retryDelay = 1000;
            
            while (retryCount < maxRetries) {
                try {
                    await websocketService.connect(projectId);
                    return; // Connection successful
                } catch (error) {
                    retryCount++;
                    console.error(`[AssetProcessor] Connection attempt ${retryCount} failed:`, error);
                    
                    if (retryCount === maxRetries) {
                        throw error;
                    }
                    
                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }
        } catch (error) {
            console.error('[AssetProcessor] Failed to set up WebSocket connection:', error);
            setError('Failed to set up WebSocket connection. Please try again.');
            setIsLoading(false);
            throw error;
        }
    }, [projectId, updateStatus]);

    const handleProcessProject = async () => {
        if (!projectId.trim()) {
            setError('Please enter a project ID');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            
            let response;
            
            // Use enhanced processing with profiles if a profile is selected
            if (selectedProfile && selectedProfile.trim()) {
                console.log('[AssetProcessor] Using enhanced processing with profile:', selectedProfile);
            
                // Find the selected profile to get its details
                const selectedProfileData = availableProfiles.find(p => 
                    (p.id || p.profile_id || p.name) === selectedProfile
                );
                
                // Prepare the request with profile information
                const request = {
                    priority: 1, // Default priority
                    metadata: {
                        selected_profiles: {
                            [selectedProfile]: {
                                profile_id: selectedProfile,
                                version: selectedVersion || '1.0.0',
                                asset_type: selectedProfileData?.asset_type || selectedProfileData?.metadata?.asset_type || 'mixed',
                                prompt_type: selectedProfileData?.prompt_type || selectedProfileData?.category || 'image'
                            }
                        },
                        user_id: 'frontend_user',
                        processing_mode: 'mixed_assets'
                    }
                };
                
                console.log('[AssetProcessor] Enhanced processing request:', request);
                
                // Use the enhanced processing method
                response = await assetService.processProjectWithProfiles(projectId, request);
                console.log('[AssetProcessor] Enhanced processing started:', response);
            } else {
                // Fallback to regular processing if no profile is selected
                console.log('[AssetProcessor] Using regular processing (no profile selected)');
                response = await assetService.processProject(projectId);
                console.log('[AssetProcessor] Regular processing started:', response);
            }

            // Check if processing was started successfully
            if (response && (response.status || (response as any).processing_result)) {
                console.log('[AssetProcessor] Processing status received:', response);
                setProcessingStatus(response);
            } else {
                console.log('[AssetProcessor] No status in response, but processing may have started');
                // Even if no status is returned, the processing might have started
                // We'll wait for WebSocket updates or manual status checks
            }

            // Set up WebSocket connection for real-time updates
            console.log('[AssetProcessor] Setting up WebSocket connection for project:', projectId);
            try {
                await setupWebSocketConnection();
                console.log('[AssetProcessor] WebSocket connection established successfully');
            } catch (wsError) {
                console.error('[AssetProcessor] WebSocket connection failed:', wsError);
                setError('Processing started but WebSocket connection failed. You may not see real-time updates.');
                // Don't set isLoading to false here - let the WebSocket onComplete handle it
            }

        } catch (error) {
            console.error('[AssetProcessor] Error starting processing:', error);
            setError(error instanceof Error ? error.message : 'Failed to start processing');
            setIsLoading(false);
        }
    };

    // Cleanup on unmount or when projectId changes
    useEffect(() => {
        let isMounted = true;
        let cleanupTimeout: NodeJS.Timeout | null = null;
        
        const cleanup = async () => {
            if (!isMounted) return;
            
            console.log('[AssetProcessor] Cleaning up WebSocket connection');
            if (cleanupTimeout) {
                clearTimeout(cleanupTimeout);
            }
            
            try {
                await websocketService.disconnect();
            } catch (error) {
                console.error('[AssetProcessor] Error during cleanup:', error);
            }
        };
        
        return () => {
            isMounted = false;
            cleanupTimeout = setTimeout(cleanup, 5000);
        };
    }, [projectId]);

    const getStatusColor = (status: string): 'completed' | 'failed' | 'processing' | 'info' | 'stopped' => {
        switch (status) {
            case 'completed':
                return 'completed';
            case 'failed':
                return 'failed';
            case 'processing':
                return 'processing';
            case 'stopped':
                return 'stopped';
            default:
                return 'info';
        }
    };

    // Log when token usage changes
    useEffect(() => {
        if (isProcessingComplete && tokenUsage) {
            console.log('[AssetProcessor] Ready to display token usage:', tokenUsage);
        }
    }, [isProcessingComplete, tokenUsage]);

    return (
        <Container>
            <BackLink to="/search">
                ← Back to Search
            </BackLink>
            
            <Card>
                <Title>Process Project Assets</Title>
                <div style={{ marginBottom: '15px' }}>
                    <Link to="/enhanced-process" style={{ color: '#007bff', textDecoration: 'none' }}>
                        → Try Enhanced Batch Processing
                    </Link>
                </div>
                
                {/* Profile and Version Selection */}
                {availableProfiles.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Prompt Profile:
                        </label>
                        <select
                            value={selectedProfile}
                            onChange={(e) => {
                                setSelectedProfile(e.target.value);
                                const profile = availableProfiles.find(p => 
                                    (p.id || p.profile_id || p.name) === e.target.value
                                );
                                setSelectedVersion(profile?.version || '1.0.0');
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '16px',
                                marginBottom: '10px'
                            }}
                            disabled={profilesLoading}
                        >
                            <option value="">Use Default Prompt (No Custom Profile)</option>
                            {availableProfiles.map((profile) => {
                                const profileId = profile.id || profile.profile_id || profile.name;
                                const profileName = profile.name || profile.description || 'Unnamed Profile';
                                const assetType = profile.asset_type || profile.metadata?.asset_type || '';
                                const promptType = profile.prompt_type || profile.category || '';
                                const isDefault = profile.is_default || false;
                                
                                return (
                                    <option key={profileId} value={profileId}>
                                        {profileName}
                                        {promptType ? ` (${promptType})` : ''}
                                        {assetType && assetType !== 'mixed' ? ` - ${assetType}` : ''}
                                        {isDefault ? ' - Default' : ''}
                                </option>
                                );
                            })}
                        </select>
                        
                        {selectedProfile && (
                            <div style={{ 
                                padding: '10px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                <strong>Selected Profile:</strong> {selectedProfile}
                                {selectedVersion && ` - Version: ${selectedVersion}`}
                                <br />
                                <small style={{ color: '#888' }}>
                                    This profile will be used for enhanced processing with custom prompts.
                                </small>
                            </div>
                        )}
                        
                        {!selectedProfile && (
                            <div style={{ 
                                padding: '10px', 
                                backgroundColor: '#fff3cd', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: '#856404',
                                border: '1px solid #ffeaa7'
                            }}>
                                <strong>Note:</strong> No profile selected. Processing will use default prompts.
                            </div>
                        )}
                    </div>
                )}
                
                {profilesLoading && (
                    <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                        Loading available profiles...
                    </div>
                )}

                <InputGroup>
                    <Input
                        type="number"
                        value={projectId}
                        onChange={(e) => setProjectId(e.target.value)}
                        placeholder="Enter Project ID"
                    />
                    <Button onClick={handleProcessProject} disabled={isLoading || !projectId.trim()}>
                        {isLoading ? 'Processing...' : 'Process'}
                    </Button>
                </InputGroup>
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </Card>

            {processingStatus && (
                <StatusCard>
                    <StatusTitle>Processing Status</StatusTitle>
                    <div style={{ marginBottom: '20px' }}>
                        {'status' in processingStatus && typeof processingStatus.status === 'object' ? (
                            <>
                                <p>Total Assets: {processingStatus.status.total_assets}</p>
                                <p>Processed: {processingStatus.status.status_summary.completed}</p>
                                <p>Failed: {processingStatus.status.status_summary.failed}</p>
                                <p>In Progress: {processingStatus.status.status_summary.processing}</p>
                            </>
                        ) : (
                            <p>Processing started successfully. Waiting for updates...</p>
                        )}
                        {'status' in processingStatus && typeof processingStatus.status === 'object' && (
                            <ProgressBar
                                $progress={
                                    ((processingStatus.status.status_summary.completed +
                                      processingStatus.status.status_summary.failed +
                                      processingStatus.status.status_summary.stopped) /
                                        processingStatus.status.total_assets) *
                                    100
                                }
                            />
                        )}
                    </div>
                    
                    {/* Project-level Token Usage Display - Only show when processing is complete */}
                    {isProcessingComplete && tokenUsage && (
                        <div style={{ marginBottom: '20px' }}>
                            <TokenUsageDisplay 
                                tokenUsage={tokenUsage} 
                                title="Project Total Token Usage"
                                showCost={false}
                            />
                        </div>
                    )}
                    
                    <StatusList>
                        {assets.map((asset) => (
                            <StatusItem key={asset.id}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <AssetTitle>{asset.title || `Asset ${asset.id}`}</AssetTitle>
                                    <StatusBadge $status={getStatusColor(asset.status)}>
                                        {asset.status}
                                    </StatusBadge>
                                    {asset.metadata?.error && (
                                        <ErrorMessage>{asset.metadata.error}</ErrorMessage>
                                    )}
                                </div>
                            </StatusItem>
                        ))}
                    </StatusList>
                </StatusCard>
            )}
        </Container>
    );
};

export default AssetProcessor;